import { z } from "zod";
import { historicalCatalogueReferenceSchema } from "./catalogue-management.js";
import {
  evidenceGateStageSchema,
  passportStableIdSchema,
  stampApplicabilitySchema,
} from "./passport-catalogue.js";
import { skillStatusSchema } from "./progress.js";

export const knowledgeCoverageSchema = z.enum(["not_started", "covered_by_learning", "assessed"]);
export const observationOutcomeSchema = z.enum(["observed", "not_applicable", "deferred"]);
export const stampEvidenceTypeSchema = z.enum([
  "knowledge_assessment",
  "learning_completion",
  "practice",
  "simulation",
  "external_certificate",
  "learner_assertion",
  "observation_request",
  "supervisor_observation",
  "supervisor_decision",
  "seed",
]);

export const stampEvidenceFacetSchema = z
  .object({
    requiredAt: evidenceGateStageSchema,
    satisfied: z.boolean(),
    satisfiedAt: z.string().datetime().nullable(),
    evidenceIds: z.array(z.string().min(1)),
  })
  .strict();
export const stampEvidenceFacetsSchema = z
  .object({
    knowledge: stampEvidenceFacetSchema,
    practice: stampEvidenceFacetSchema,
    behaviour: stampEvidenceFacetSchema,
  })
  .strict();

export const atomicCoverageStateSchema = z.enum(["uncovered", "partial", "covered"]);
export const atomicRequirementCoverageSchema = z
  .object({
    requirementId: passportStableIdSchema,
    kind: z.enum(["skill", "knowledge", "behaviour"]),
    state: atomicCoverageStateSchema,
    score: z.number().int().min(0).max(3),
    sourceIds: z.array(z.string().min(1)),
  })
  .strict();
export const stampAtomicCoverageSchema = z
  .object({
    percent: z.number().int().min(0).max(100),
    thresholdPercent: z.number().int().min(1).max(100),
    thresholdMet: z.boolean(),
    mandatoryMet: z.boolean(),
    policyMet: z.boolean(),
    covered: z.number().int().nonnegative(),
    partial: z.number().int().nonnegative(),
    uncovered: z.number().int().nonnegative(),
    total: z.number().int().nonnegative(),
    mandatoryBlockerIds: z.array(passportStableIdSchema),
    requirements: z.array(atomicRequirementCoverageSchema),
  })
  .strict();

export const passportActionCountersSchema = z.object({
  knowledgeAssessments: z.number().int().nonnegative(),
  learningStarts: z.number().int().nonnegative(),
  learningCompletions: z.number().int().nonnegative(),
  knowledgeTests: z.number().int().nonnegative(),
  practicalEvidenceSubmissions: z.number().int().nonnegative(),
  observationRequests: z.number().int().nonnegative(),
  checklistSubmissions: z.number().int().nonnegative(),
  supervisorConfirms: z.number().int().nonnegative(),
  supervisorKeeps: z.number().int().nonnegative(),
  supervisorReturns: z.number().int().nonnegative(),
});

export const employeeStampProgressSchema = z
  .object({
    employeeId: z.string().min(1),
    stampId: passportStableIdSchema,
    status: skillStatusSchema,
    applicability: stampApplicabilitySchema,
    knowledgeCoverage: knowledgeCoverageSchema,
    facets: stampEvidenceFacetsSchema,
    atomicCoverage: stampAtomicCoverageSchema,
    submittedAt: z.string().datetime().nullable(),
    supervisorFeedback: z.string().nullable(),
    catalogue: historicalCatalogueReferenceSchema,
  })
  .strict();

export const stampEvidenceSchema = z
  .object({
    id: z.string().min(1),
    employeeId: z.string().min(1),
    pageId: passportStableIdSchema,
    stampId: passportStableIdSchema,
    type: stampEvidenceTypeSchema,
    source: z.string().min(1),
    context: z.string().min(1),
    actorId: z.string().min(1),
    createdAt: z.string().datetime(),
    catalogue: historicalCatalogueReferenceSchema,
  })
  .strict();

export const behaviourObservationSchema = z
  .object({
    criterionId: passportStableIdSchema,
    outcome: observationOutcomeSchema,
    note: z.string().trim().max(1_000).nullable(),
  })
  .strict()
  .superRefine((value, context) => {
    if (value.outcome !== "observed" && !value.note) {
      context.addIssue({
        code: "custom",
        path: ["note"],
        message: "Een toelichting is verplicht bij niet van toepassing of uitstel.",
      });
    }
  });

export type EmployeeStampProgress = z.infer<typeof employeeStampProgressSchema>;
export type StampEvidence = z.infer<typeof stampEvidenceSchema>;
export type BehaviourObservation = z.infer<typeof behaviourObservationSchema>;
export type PassportActionCounters = z.infer<typeof passportActionCountersSchema>;
export type AtomicRequirementCoverage = z.infer<typeof atomicRequirementCoverageSchema>;
export type StampAtomicCoverage = z.infer<typeof stampAtomicCoverageSchema>;
